import * as styles from './game-result-events.list.styles';

import { Pencil, Trophy } from 'lucide-react';
import { GameDetailsTeamMember, GameEvent, GameEventType } from '../../../domain/interfaces/game-details';

import EmptyButton from 'ud-ui/components/empty-button';
import FootballCardIcon from 'ud-ui/components/icon/football-card-icon';
import FootballCornerIcon from 'ud-ui/components/icon/football-corner-icon';
import FootballIcon from 'ud-ui/components/icon/football-icon';
import FootballPassAndGoalIcon from 'ud-ui/components/icon/football-pass-and-goal-icon';
import FootballPenaltyIcon from 'ud-ui/components/icon/football-penalty-icon';
import FreeKickIcon from 'ud-ui/components/icon/free-kick-icon';
import MossedPenaltyIcon from 'ud-ui/components/icon/missed-penalty-icon';
import OwnGoalIcon from 'ud-ui/components/icon/own-goal-icon';
import ReplacementIcon from 'ud-ui/components/icon/replacement-icon';
import TimeoutIcon from 'ud-ui/components/icon/timeout-icon';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'styles/theme/theme';

type GameResultEventIconProps = {
  type: GameEventType;
};

export const GameResultEventIcon = (props: GameResultEventIconProps) => {
  const width = 20;
  const height = 20;

  switch (props.type.toLowerCase()) {
    case GameEventType.GOAL.toLowerCase():
    case GameEventType.AFTER_MATCH_PENALTY_GOAL.toLowerCase():
      return <FootballIcon width={width} height={height} />;
    case GameEventType.PASS_AND_GOAL.toLowerCase():
      return <FootballPassAndGoalIcon width={30} height={28} />;
    case GameEventType.CORNER.toLowerCase():
      return <FootballCornerIcon width={24} height={28} />;
    case GameEventType.FOUL.toLowerCase():
      return <FreeKickIcon width={20} height={26} />;
    case GameEventType.PENALTY.toLowerCase():
      return <FootballPenaltyIcon width={31} height={24} />;
    case GameEventType.RED_CARD.toLowerCase():
      return <FootballCardIcon width={width} height={height} type="red" />;
    case GameEventType.YELLOW_CARD.toLowerCase():
      return <FootballCardIcon width={width} height={height} type="yellow" />;
    case GameEventType.REPLACEMENT.toLowerCase():
      return <ReplacementIcon width={width} height={height} />;
    case GameEventType.TIMEOUT.toLowerCase():
      return <TimeoutIcon width={20} height={25} />;
    case GameEventType.OWN_GOAL.toLowerCase():
      return <OwnGoalIcon width={40} height={14} />;
    case GameEventType.AFTER_MATCH_PENALTY_MISSED.toLowerCase():
      return <MossedPenaltyIcon width={16} height={16} />;
    default:
      return <Trophy width={width} height={height} color={theme.colors.AccentNaplesYellow} />;
  }
};

type GameResultEventProps = {
  event: GameEvent;
  members: Record<number, GameDetailsTeamMember>;
  isLeft: boolean;
  onEditClick?: (eventId: number) => void;
};

export const GameResultEvent = (props: GameResultEventProps) => {
  const { t } = useTranslation();

  const { event, members, isLeft, onEditClick } = props;
  const className = `game-event ${isLeft ? 'left' : 'right'}`;

  const handleEditClick = useCallback(() => {
    if (onEditClick) {
      onEditClick(event.id);
    }
  }, [event, onEditClick]);

  return (
    <div className={className}>
      <div></div>
      <styles.GameResultEventMinute minutes={event.minutes} extraMinutes={event.extraMinutes} isLeft={isLeft}>
        <div className="game-event-time">
          <div className="minutes">{event.minutes}’</div>
          {event.extraMinutes && <div className="extra-minutes">+{event.extraMinutes}</div>}
        </div>

        <div className="game-event-card">
          <div className="game-event-icon">
            <GameResultEventIcon type={event.type} />
          </div>
          <div className="game-event-body">
            <div className="game-event-type flex flex-row">
              {t(`games.eventTypes.${event.type.toLowerCase()}`)}
              <EmptyButton style={{ marginLeft: 8 }} onClick={handleEditClick}>
                <Pencil size={14} />
              </EmptyButton>
            </div>
            {event.playerIds.map((playerId) => (
              <div key={playerId} className="game-event-player">
                <div className="game-event-player-info">
                  {members[playerId]?.lastName ?? 'Неизвестный'} {members[playerId]?.firstName ?? 'Неизвестный'}
                </div>
                <div className="game-event-player-position">
                  #{members[playerId]?.number ?? '?'},{' '}
                  {members[playerId] ? t(`player.position.${members[playerId]?.position}`) : 'Неизвестно'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </styles.GameResultEventMinute>
      <div></div>
    </div>
  );
};
